// The reports index (docs/plans/phase1-reports.md §9.1): grouped plain lists, only what
// the reader may open. A report that isn't available yet says why, in words.
import { ANALYTICS_AREAS, type AnalyticsArea } from '@wodi/domain';
import {
  ANALYTICS_ACCESS,
  REPORT_ACCESS,
  type Permission,
  type ReportKey,
  type SessionView,
} from '@wodi/shared';

import { can } from '../api/session.ts';

export type ReportHref =
  | '/reports/day-summary'
  | '/reports/daily-collections'
  | '/reports/revenue'
  | '/reports/payer-share-unclaimed'
  | '/reports/opd-register'
  | '/reports/lab-register'
  | '/reports/pharmacy-register'
  | '/reports/moh/$form'
  | '/reports/khis'
  | '/reports/quality'
  | '/reports/analytics/$area';

export interface ReportEntry {
  title: string;
  description: string;
  /** What must be held to see the entry at all. */
  permissions: readonly Permission[];
  /** Null when the report can't be opened yet; `waitsFor` says why. */
  href: ReportHref | null;
  params?: { form: MohFormKey } | { area: AnalyticsArea };
  waitsFor?: string;
}

export interface ReportGroup {
  title: string;
  entries: ReportEntry[];
}

export const MOH_FORMS = {
  '705a': { title: 'MOH 705A', description: 'Outpatient morbidity, under 5 years.' },
  '705b': { title: 'MOH 705B', description: 'Outpatient morbidity, 5 years and over.' },
  '717': { title: 'MOH 717', description: 'Hospital workload, outpatient parts.' },
} as const;
export type MohFormKey = keyof typeof MOH_FORMS;

const REPORT_HREF: Record<ReportKey, ReportHref> = {
  'day-summary': '/reports/day-summary',
  'daily-collections': '/reports/daily-collections',
  revenue: '/reports/revenue',
  'payer-share-unclaimed': '/reports/payer-share-unclaimed',
  'opd-register': '/reports/opd-register',
  'lab-register': '/reports/lab-register',
  'pharmacy-register': '/reports/pharmacy-register',
};

export function reportHref(key: ReportKey): ReportHref {
  return REPORT_HREF[key];
}

function entry(key: ReportKey, title: string, description: string): ReportEntry {
  return {
    title,
    description,
    permissions: REPORT_ACCESS[key].permissions,
    href: REPORT_HREF[key],
  };
}

const GROUPS: ReportGroup[] = [
  {
    title: 'Summary',
    entries: [
      entry(
        'day-summary',
        "Owner's day summary",
        'Patients, money in and out, waiting times and stock alerts for a day.',
      ),
      {
        title: 'Quality indicators',
        description:
          'Waiting time, length of stay, bed occupancy, lab turnaround, critical results, claim rejections, stock-outs and readmissions, by month.',
        permissions: ['quality.read'],
        href: '/reports/quality',
      },
    ],
  },
  {
    // Deeper analytics (docs/plans/phase3-deeper-analytics.md §1.1): one entry per area.
    title: 'Analytics',
    entries: ANALYTICS_AREAS.map((area): ReportEntry => ({
      title: ANALYTICS_ACCESS[area].title,
      description: ANALYTICS_ACCESS[area].description,
      permissions: ANALYTICS_ACCESS[area].read,
      href: '/reports/analytics/$area',
      params: { area },
    })),
  },
  {
    title: 'Money',
    entries: [
      entry(
        'daily-collections',
        'Daily collections',
        'Money received by method, cashier and shift, with refunds.',
      ),
      entry(
        'revenue',
        'Revenue by department and payer',
        'What was billed, waived, discounted and collected.',
      ),
      entry(
        'payer-share-unclaimed',
        'Claims pipeline',
        'Payer share not yet claimed, by payer and age, and SHA claims by state.',
      ),
    ],
  },
  {
    title: 'Registers',
    entries: [
      entry('opd-register', 'OPD register', 'Every outpatient visit for a day or range.'),
      entry(
        'lab-register',
        'Lab register',
        'Every lab test and sample, with turnaround and flags.',
      ),
      entry(
        'pharmacy-register',
        'Pharmacy register',
        'Every medicine dispensed or sold at the counter, with batches.',
      ),
    ],
  },
  {
    title: 'For the Ministry of Health',
    entries: [
      ...(Object.keys(MOH_FORMS) as MohFormKey[]).map((form): ReportEntry => ({
        title: MOH_FORMS[form].title,
        description: MOH_FORMS[form].description,
        permissions: ['reports.moh.read'],
        href: '/reports/moh/$form',
        params: { form },
        waitsFor: 'Needs the official form definition.',
      })),
      {
        title: 'KHIS submissions',
        description: 'Monthly counts prepared, approved and sent to KHIS, with their state.',
        permissions: ['khis.read'],
        href: '/reports/khis',
      },
      {
        title: 'Diagnoses not placed in an MOH row',
        description: 'Coded diagnoses the MOH returns could not count, with the reason.',
        permissions: ['reports.moh.read'],
        href: null,
        waitsFor: 'Needs the official form definition.',
      },
    ],
  },
];

/** The groups and entries this person may see; empty groups are left out. */
export function reportGroupsFor(session: SessionView): ReportGroup[] {
  return GROUPS.map((group) => ({
    ...group,
    entries: group.entries.filter((e) => e.permissions.every((p) => can(session, p))),
  })).filter((group) => group.entries.length > 0);
}

/** Whether to show Reports in the navigation. */
export function canSeeReports(session: SessionView): boolean {
  return reportGroupsFor(session).length > 0;
}
